import { useMemo, useState, type MouseEvent } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Grid2,
  Stack,
  Typography,
  IconButton,
  type SxProps,
  type Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { useNavigate } from "react-router";
import { Pages } from "../../utils/route-paths";
import {
  projectCategoryLabels,
  projectStyleLabels,
  formatSpecialRequirementLabel,
  bugetRangeLabels,
  projectDurationLabels,
  squareFootageLabels,
} from "../../utils/option-helpers";
import { SaveToIdeaBookPopover } from "../idea-books/save-to-idea-book-popover";
import { useUserContext } from "../../hooks/userContext";

const getHomeownerTier = (subscription: unknown) => {
  const rawSubscription =
    typeof subscription === "string"
      ? subscription
      : (subscription as any)?.homeownerValue ||
        (subscription as any)?.designerValue ||
        (subscription as any)?.value ||
        (subscription as any)?.tier ||
        "";

  if (!rawSubscription) return "basic";
  if (rawSubscription === "Free" || rawSubscription === "basic") return "basic";
  if (rawSubscription === "GuildMonthly" || rawSubscription === "Guild")
    return "Guild";
  if (
    rawSubscription === "GuildAnnually" ||
    rawSubscription === "GuildPlus" ||
    rawSubscription === "Guild+"
  )
    return "GuildPlus";
  if (
    rawSubscription === "StyleFileMonthly" ||
    rawSubscription === "StyleFileAnnually"
  )
    return "Guild";

  return "basic";
};

type ProjectImage = {
  _id?: string | null;
  url?: string | null;
  title?: string | null;
  credit?: string | null;
  description?: string | null;
};

export type ProjectFilePreviewData = {
  _id?: string | null;
  name?: string | null;
  description?: string | null;
  projectCategory?: keyof typeof projectCategoryLabels | null;
  projectStyle?: keyof typeof projectStyleLabels | null;
  specialRequirements?: (string | null | undefined)[] | null;
  budgetRange?: keyof typeof bugetRangeLabels | null;
  projectDuration?: keyof typeof projectDurationLabels | null;
  squareFootage?: keyof typeof squareFootageLabels | null;
  images?: ProjectImage[] | null;
};

interface ProjectFilePreviewProps {
  project: ProjectFilePreviewData;
  designerId?: string | null;
  showConnectButton?: boolean;
  onConnectWithDesigner?: () => void;
}

const SECTION_CARD_BASE_SX: SxProps<Theme> = {
  backgroundColor: "#fff",
  borderRadius: 3, // 12px
  px: { xs: 2, md: 3 },
  py: { xs: 2, md: 3 },
  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.06)", // Subtle, soft shadow
};

const SectionCard = ({
  title,
  action,
  children,
  sx,
}: {
  title?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}) => {
  const mergedSx = useMemo(() => {
    if (!sx) return SECTION_CARD_BASE_SX;
    if (Array.isArray(sx)) return [SECTION_CARD_BASE_SX, ...sx];
    return [SECTION_CARD_BASE_SX, sx];
  }, [sx]);

  return (
    <Box sx={mergedSx}>
      {(title || action) && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          mb={title ? 2 : 0}
          spacing={2}
        >
          {title && (
            <Typography variant="h6" fontWeight={700}>
              {title}
            </Typography>
          )}
          {action}
        </Stack>
      )}
      {children}
    </Box>
  );
};

export const ProjectFilePreview: React.FC<ProjectFilePreviewProps> = ({
  project,
  designerId,
  showConnectButton = false,
  onConnectWithDesigner,
}) => {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const [activeImageIndex, setActiveImageIndex] = useState<number | null>(null);
  const [saveAnchorEl, setSaveAnchorEl] = useState<HTMLElement | null>(null);
  const [saveImageIds, setSaveImageIds] = useState<string[]>([]);

  const gallery = project?.images ?? [];
  const activeImage = activeImageIndex !== null ? gallery[activeImageIndex] : null;
  const galleryImageIds = useMemo(
    () =>
      gallery
        .map((image) => image?._id)
        .filter((id): id is string => Boolean(id))
        .map((id) => String(id)),
    [gallery]
  );
  const isHomeowner = (user?.accountType?.toLowerCase() || "") === "homeowner";
  const subscriptionTier = useMemo(
    () => getHomeownerTier(user?.subscription),
    [user?.subscription]
  );
  const paymentStatus = ((user as any)?.paymentStatus || "")
    .toString()
    .toLowerCase();
  const hasPaidStatus = paymentStatus === "paid" || paymentStatus === "completed";
  const canSaveToIdeaBooks =
    isHomeowner &&
    (subscriptionTier === "Guild" ||
      subscriptionTier === "GuildPlus" ||
      hasPaidStatus);
  const homeownerId = user?.id ?? null;
  const showGallerySave = canSaveToIdeaBooks && galleryImageIds.length > 0;

  const handlePreviousImage = () => {
    if (activeImageIndex !== null && activeImageIndex > 0) {
      setActiveImageIndex(activeImageIndex - 1);
    }
  };

  const handleNextImage = () => {
    if (activeImageIndex !== null && activeImageIndex < gallery.length - 1) {
      setActiveImageIndex(activeImageIndex + 1);
    }
  };

  const handleOpenSavePopover = (
    event: MouseEvent<HTMLElement>,
    imageIds: string[]
  ) => {
    event.stopPropagation();
    if (!canSaveToIdeaBooks || imageIds.length === 0) return;
    setSaveAnchorEl(event.currentTarget);
    setSaveImageIds(imageIds);
  };

  const handleCloseSavePopover = () => {
    setSaveAnchorEl(null);
    setSaveImageIds([]);
  };

  const metaItems = useMemo(() => {
    if (!project) return [];

    const items: { label: string; value: string }[] = [];
    if (project.projectCategory) {
      items.push({
        label: "Project Type",
        value:
          projectCategoryLabels[project.projectCategory] ?? project.projectCategory,
      });
    }
    if (project.projectStyle) {
      items.push({
        label: "Project Style",
        value:
          projectStyleLabels[project.projectStyle] ?? project.projectStyle,
      });
    }
    if (project.budgetRange) {
      items.push({
        label: "Budget Range",
        value: bugetRangeLabels[project.budgetRange] ?? project.budgetRange,
      });
    }
    if (project.projectDuration) {
      items.push({
        label: "Time to Complete",
        value:
          projectDurationLabels[project.projectDuration] ??
          project.projectDuration,
      });
    }
    if (project.squareFootage) {
      items.push({
        label: "Square Footage",
        value:
          squareFootageLabels[project.squareFootage] ?? project.squareFootage,
      });
    }
    return items;
  }, [project]);

  const specialRequirements = useMemo(() => {
    if (!project?.specialRequirements?.length) return [];
    return project.specialRequirements
      .map((requirement) =>
        requirement ? formatSpecialRequirementLabel(requirement) : null
      )
      .filter((value): value is string => Boolean(value));
  }, [project]);

  const handleConnectWithDesigner = () => {
    if (onConnectWithDesigner) {
      onConnectWithDesigner();
      return;
    }

    if (designerId) {
      navigate(Pages.DESIGNER_PREMIUM_PROFILE.replace(":designerId", designerId));
      return;
    }

    navigate(-1);
  };

  return (
    <>
      <Stack spacing={{ xs: 3, md: 4 }}>
        {/* Single Wrapper for Project Title, Details, and Description */}
        <SectionCard>
          <Stack spacing={4}>
            {/* Project Title */}
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "2rem", md: "2.5rem" },
                fontWeight: 600,
                color: "#4A5568",
                lineHeight: 1.2,
              }}
            >
              {project.name || "Untitled Project"}
            </Typography>

            {/* Project Details - Horizontal Layout (1 row, 4 columns) */}
            <Grid container spacing={3}>
              {metaItems.map((item) => (
                <Grid key={item.label} item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.875rem",
                        fontWeight: 400,
                        mb: 0.5,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#5C3BFF",
                        fontSize: "1rem",
                        fontWeight: 600,
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Special Requirements */}
            {specialRequirements.length > 0 && (
              <Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {specialRequirements.map((requirement, index) => (
                    <Chip
                      key={`${requirement}-${index}`}
                      label={requirement}
                      size="small"
                      sx={{
                        backgroundColor: "rgba(92, 59, 255, 0.12)",
                        color: "#5C3BFF",
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Project Description */}
            <Box>
              <Typography variant="h6" fontWeight={700} mb={1}>
                Project Description
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ maxWidth: "600px" }}
                dangerouslySetInnerHTML={{
                  __html: project.description?.trim()
                    ? project.description
                    : "No project description has been added yet."
                }}
              />
            </Box>
          </Stack>
        </SectionCard>

        <SectionCard
          title="Project Gallery"
          action={
            showGallerySave ? (
              <Button
                variant="outlined"
                startIcon={<FavoriteBorderIcon sx={{ fontSize: 16 }} />}
                onClick={(event) =>
                  handleOpenSavePopover(event, galleryImageIds)
                }
                sx={{
                  textTransform: "none",
                  backgroundColor: "#fff",
                  color: "#1F2933",
                  borderColor: "#E5E7EB",
                  fontWeight: 500,
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "#F9FAFB",
                    borderColor: "#D1D5DB",
                  },
                }}
              >
                Save to Idea Book
              </Button>
            ) : undefined
          }
        >
          {gallery.length ? (
            <Grid container spacing={2}>
              {gallery.map((image, index) => (
                <Grid key={image?._id ?? `${project._id}-image-${index}`} item xs={6} sm={4} md={3}>
                  <Box
                    role={image?.url ? "button" : undefined}
                    tabIndex={image?.url ? 0 : -1}
                    onClick={() => image?.url && setActiveImageIndex(index)}
                    onKeyDown={(event) => {
                      if (
                        image?.url &&
                        (event.key === "Enter" || event.key === " ")
                      ) {
                        event.preventDefault();
                        setActiveImageIndex(index);
                      }
                    }}
                    sx={{
                      position: "relative",
                      borderRadius: 2,
                      overflow: "hidden",
                      aspectRatio: "1 / 1",
                      cursor: image?.url ? "pointer" : "default",
                      backgroundColor: "#F3F4F6",
                      backgroundImage: image?.url
                        ? `url(${image.url})`
                        : undefined,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: image?.url ? "scale(1.03)" : "none",
                      },
                    }}
                  >
                    {canSaveToIdeaBooks && image?._id && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FavoriteBorderIcon sx={{ fontSize: 16 }} />}
                        onClick={(event) =>
                          handleOpenSavePopover(event, [String(image._id)])
                        }
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          textTransform: "none",
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          color: "#1F2933",
                          borderColor: "#E5E7EB",
                          fontWeight: 400,
                          borderRadius: "8px",
                          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                          "&:hover": {
                            backgroundColor: "#fff",
                            borderColor: "#D1D5DB",
                          },
                        }}
                      >
                        Save
                      </Button>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No gallery images have been added to this project yet.
            </Typography>
          )}
        </SectionCard>

        {showConnectButton && (
          <SectionCard>
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography 
                variant="h5" 
                fontWeight={700} 
                sx={{ mb: 2, color: "text.primary" }}
              >
                Ready to Transform Your Space?
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ mb: 3, color: "text.secondary" }}
              >
                Let's create something beautiful together
              </Typography>
              <Button
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: "#E91E63",
                  color: "white",
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#C2185B",
                  },
                }}
                onClick={handleConnectWithDesigner}
              >
                Connect With Designer
              </Button>
            </Box>
          </SectionCard>
        )}
      </Stack>

      {activeImage?.url && (
        <Dialog
          open
          onClose={() => setActiveImageIndex(null)}
          fullScreen
          PaperProps={{
            sx: {
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #E0E0E0",
              pb: 2,
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              {project?.name}
            </Typography>
            <IconButton
              onClick={() => setActiveImageIndex(null)}
              sx={{
                color: "text.primary",
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <Grid2 container sx={{ height: "100%", flex: 1 }}>
              {/* Left Column - Image */}
              <Grid2 size={{ xs: 12, md: 6 }} sx={{ height: "100%", overflow: "hidden" }}>
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#F5F5F5",
                    p: 3,
                  }}
                >
                  {canSaveToIdeaBooks && activeImage?._id && (
                    <Button
                      variant="outlined"
                      startIcon={<FavoriteBorderIcon sx={{ fontSize: 16 }} />}
                      onClick={(event) =>
                        handleOpenSavePopover(event, [String(activeImage._id)])
                      }
                      sx={{
                        position: "absolute",
                        top: 20,
                        right: 20,
                        textTransform: "none",
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        color: "#1F2933",
                        borderColor: "#E5E7EB",
                        fontWeight: 500,
                        borderRadius: "8px",
                        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                        "&:hover": {
                          backgroundColor: "#fff",
                          borderColor: "#D1D5DB",
                        },
                      }}
                    >
                      Save to Idea Book
                    </Button>
                  )}
                  <Box
                    component="img"
                    src={activeImage.url ?? undefined}
                    alt={project?.name ?? "Project image"}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Grid2>
              
              {/* Right Column - Image Title and Description */}
              <Grid2 size={{ xs: 12, md: 6 }} sx={{ height: "100%", overflow: "auto" }}>
                <Box sx={{ p: 4, height: "100%" }}>
                  {activeImage.title && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="overline"
                        sx={{ color: "text.secondary", letterSpacing: 1 }}
                      >
                        Image Title
                      </Typography>
                      <Typography
                        variant="h4"
                        fontWeight={600}
                        sx={{ color: "text.primary" }}
                      >
                        {activeImage.title}
                      </Typography>
                    </Box>
                  )}
                  {activeImage.credit && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="overline"
                        sx={{ color: "text.secondary", letterSpacing: 1 }}
                      >
                        Image Credit
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {activeImage.credit}
                      </Typography>
                    </Box>
                  )}
                  {activeImage.description && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="overline"
                        sx={{ color: "text.secondary", letterSpacing: 1 }}
                      >
                        Image Description
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "text.secondary",
                          lineHeight: 1.7,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {activeImage.description}
                      </Typography>
                    </Box>
                  )}
                  {!activeImage.title && !activeImage.description && (
                    <Typography variant="body2" color="text.secondary">
                      No additional details available for this image.
                    </Typography>
                  )}
                </Box>
              </Grid2>
            </Grid2>
          </DialogContent>
          {/* Fixed Footer with Navigation Arrows */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              left: 0,
              right: 0,
              borderTop: "1px solid #E0E0E0",
              backgroundColor: "#fff",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2,
              gap: 2,
            }}
          >
            <IconButton
              onClick={handlePreviousImage}
              disabled={activeImageIndex === null || activeImageIndex === 0}
              sx={{
                color: activeImageIndex === null || activeImageIndex === 0 ? "text.disabled" : "text.primary",
                "&:hover": {
                  backgroundColor: activeImageIndex === null || activeImageIndex === 0 ? "transparent" : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              {activeImageIndex !== null ? `${activeImageIndex + 1} / ${gallery.length}` : ""}
            </Typography>
            <IconButton
              onClick={handleNextImage}
              disabled={activeImageIndex === null || activeImageIndex === gallery.length - 1}
              sx={{
                color: activeImageIndex === null || activeImageIndex === gallery.length - 1 ? "text.disabled" : "text.primary",
                "&:hover": {
                  backgroundColor: activeImageIndex === null || activeImageIndex === gallery.length - 1 ? "transparent" : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Dialog>
      )}

      <SaveToIdeaBookPopover
        open={Boolean(saveAnchorEl)}
        anchorEl={saveAnchorEl}
        onClose={handleCloseSavePopover}
        homeownerId={homeownerId}
        imageIds={saveImageIds}
      />
    </>
  );
};
